import React, { useState, useEffect } from "react";
import { getSuiviTableauDeBord } from "../services/api";

// ===================================================================
// The Crowd v2 — Gestion utilisateurs › Suivi : tableau de bord
//
// Managers et directeurs (le backend le verifie). Sur 7, 30 ou 90 jours :
// - indicateurs : total, moyenne par jour ouvre, jour le plus charge ;
// - evolution quotidienne : une colonne par jour, empilee par type ;
// - repartition par type : nombre et pourcentage ;
// - total par technicien.
// Props : collaborateurId, rafraichir (change apres chaque saisie)
// ===================================================================

export const TYPES_OPERATIONS = [
  { cle: "creations", libelle: "Créations", classe: "v2-suivi-type-1" },
  { cle: "modifications", libelle: "Modifications", classe: "v2-suivi-type-2" },
  { cle: "desactivations", libelle: "Désactivations", classe: "v2-suivi-type-3" },
];

const PERIODES = [7, 30, 90];

function dateCourte(iso, avecJour = false) {
  const d = new Date(`${String(iso).split("T")[0]}T00:00:00Z`);
  const opts = avecJour
    ? { weekday: "short", day: "numeric", month: "short", timeZone: "UTC" }
    : { day: "numeric", month: "short", timeZone: "UTC" };
  return d.toLocaleDateString("fr-FR", opts);
}

function Legende() {
  return (
    <span className="v2-suivi-legende">
      {TYPES_OPERATIONS.map((t) => (
        <span key={t.cle} className="v2-suivi-legende-item">
          <span className={`v2-suivi-pastille ${t.classe}`} aria-hidden="true" />
          {t.libelle}
        </span>
      ))}
    </span>
  );
}

function Evolution({ jours }) {
  const max = Math.max(1, ...jours.map((j) => j.total));
  // Libelles sous les colonnes : tous sur 7 jours, un sur sept au-dela
  const pas = jours.length <= 7 ? 1 : 7;
  return (
    <div className="v2-suivi-colonnes" role="img" aria-label="Évolution quotidienne des opérations">
      {jours.map((j, i) => (
        <div
          key={j.date}
          className="v2-suivi-colonne"
          title={`${dateCourte(j.date, true)} : ${j.total} opération${j.total > 1 ? "s" : ""} (${TYPES_OPERATIONS.map((t) => `${t.libelle.toLowerCase()} ${j[t.cle]}`).join(", ")})`}
        >
          <div className="v2-suivi-pile">
            {TYPES_OPERATIONS.map((t) => (j[t.cle] > 0 ? (
              <span key={t.cle} className={`v2-suivi-segment ${t.classe}`} style={{ height: `${(j[t.cle] / max) * 100}%` }} />
            ) : null))}
          </div>
          <span className="v2-suivi-colonne-libelle">
            {(i % pas === 0 || i === jours.length - 1) ? dateCourte(j.date, jours.length <= 7) : ""}
          </span>
        </div>
      ))}
    </div>
  );
}

function SuiviTableauDeBordV2({ collaborateurId, rafraichir }) {
  const [jours, setJours] = useState(30);
  const [donnees, setDonnees] = useState(null);
  const [erreur, setErreur] = useState(null);

  useEffect(() => {
    let annule = false;
    setErreur(null);
    getSuiviTableauDeBord(collaborateurId, jours)
      .then((d) => { if (!annule) setDonnees(d); })
      .catch((err) => { if (!annule) setErreur(`Tableau de bord non chargé : ${err.message}`); });
    return () => { annule = true; };
  }, [collaborateurId, jours, rafraichir]);

  const ind = donnees?.indicateurs;

  return (
    <section className="v2-suivi-tdb" aria-labelledby="v2-suivi-tdb-titre">
      <div className="v2-section-titre">
        <h2 id="v2-suivi-tdb-titre">Tableau de bord</h2>
        <span className="v2-section-filet" />
        <div className="v2-bascule" role="group" aria-label="Période">
          {PERIODES.map((p) => (
            <button key={p} type="button" className={`v2-bascule-choix${jours === p ? " actif" : ""}`} aria-pressed={jours === p} onClick={() => setJours(p)}>
              {p} jours
            </button>
          ))}
        </div>
      </div>

      {erreur && <div className="v2-msg v2-msg-erreur v2-marge-bas">{erreur}</div>}
      {!donnees && !erreur && <div className="v2-chargement">Chargement…</div>}

      {donnees && (
        <>
          <div className="v2-dash-stats v2-suivi-stats">
            <div className="v2-stat">
              <span className="v2-stat-libelle">Opérations</span>
              <span className="v2-stat-valeur">{ind.total}</span>
              <span className="v2-stat-aide">Sur {jours} jours</span>
            </div>
            <div className="v2-stat">
              <span className="v2-stat-libelle">Moyenne par jour ouvré</span>
              <span className="v2-stat-valeur">{String(ind.moyenne_jour_ouvre).replace(".", ",")}</span>
            </div>
            <div className="v2-stat">
              <span className="v2-stat-libelle">Jour le plus chargé</span>
              <span className="v2-stat-valeur">{ind.jour_max ? ind.jour_max.total : "—"}</span>
              <span className="v2-stat-aide">{ind.jour_max ? dateCourte(ind.jour_max.date, true) : "Aucune saisie"}</span>
            </div>
          </div>

          <div className="v2-suivi-grille">
            <div className="v2-panneau v2-suivi-panneau-large">
              <div className="v2-suivi-panneau-entete">
                <h3 className="v2-panneau-titre">Évolution quotidienne</h3>
                <Legende />
              </div>
              {ind.total === 0
                ? <p className="v2-vide">Aucune opération saisie sur la période.</p>
                : <Evolution jours={donnees.par_jour} />}
            </div>

            <div className="v2-panneau">
              <h3 className="v2-panneau-titre">Répartition par type</h3>
              <ul className="v2-suivi-repartition">
                {TYPES_OPERATIONS.map((t) => {
                  const r = donnees.repartition.find((x) => x.type === t.cle) || { nombre: 0, pourcentage: 0 };
                  return (
                    <li key={t.cle}>
                      <span className="v2-suivi-repartition-libelle">
                        <span className={`v2-suivi-pastille ${t.classe}`} aria-hidden="true" />
                        {t.libelle}
                      </span>
                      <span className="v2-suivi-barre-piste">
                        <span className={`v2-suivi-barre-valeur ${t.classe}`} style={{ width: `${r.pourcentage}%` }} />
                      </span>
                      <span className="v2-suivi-repartition-valeur">
                        <strong>{r.nombre}</strong> · {String(r.pourcentage).replace(".", ",")} %
                      </span>
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>

          <div className="v2-panneau v2-tableau-conteneur">
            <h3 className="v2-panneau-titre">Par technicien</h3>
            {donnees.par_technicien.length === 0 ? (
              <p className="v2-vide">Aucune saisie sur la période.</p>
            ) : (
              <table className="v2-tableau">
                <thead>
                  <tr>
                    <th scope="col">Technicien</th>
                    {TYPES_OPERATIONS.map((t) => <th key={t.cle} scope="col" className="v2-num">{t.libelle}</th>)}
                    <th scope="col" className="v2-num">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {donnees.par_technicien.map((p) => (
                    <tr key={p.collaborateur_id}>
                      <td className="v2-gras">{p.nom}</td>
                      {TYPES_OPERATIONS.map((t) => <td key={t.cle} className="v2-num">{p[t.cle]}</td>)}
                      <td className="v2-num v2-gras">{p.total}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}
    </section>
  );
}

export default SuiviTableauDeBordV2;
